import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { OpBanner } from 'new-components/DLS/OpBanner/OpBanner';
import { useLocalStorage } from 'utils/customHooks/useLocalStorage';

import {
  isBrowserTimezoneDataStale,
  resolveBrowserTimeZone,
} from './helpers/isBrowserTimezoneDataStale';

// Stale rules are a property of this browser, so the dismissal is stored here
// rather than on the identity -- dismissing on an up-to-date machine must not
// hide the warning on the one that is actually wrong. Keyed by zone only, so a
// browser that is never updated stays quiet once the user has acknowledged it.
const DISMISSED_ZONES_STORAGE_KEY = 'outdatedTimezoneDataDismissedZones';

/**
 * Warns the user when their browser's time zone rules are out of date, which
 * silently shifts the access dates they pick by an hour. Shown only to the
 * users whose own zone is affected, since that is whose dates are wrong.
 *
 * TEMPORARY - this banner is a stopgap for the tzdata 2026e rollout and is
 * meant to be deleted once browsers have caught up. To remove it, delete this
 * directory and the `<OutdatedTimezoneDataBanner />` render in both
 * `new-components/OpAppBanners/OpAppBanners` and `containers/BannerContainer`.
 */
export const OutdatedTimezoneDataBanner = () => {
  const { t } = useTranslation();
  const [dismissedZones, setDismissedZones] = useLocalStorage<string[]>(
    DISMISSED_ZONES_STORAGE_KEY,
    [],
  );

  const timeZone = useMemo(() => resolveBrowserTimeZone(), []);
  // A dozen offset comparisons, so it runs once per mount rather than per render.
  const isStale = useMemo(
    () => isBrowserTimezoneDataStale({ timeZone }),
    [timeZone],
  );

  // This banner mounts in every app shell, so a malformed stored value must not
  // be able to take the whole portal down.
  const dismissed = Array.isArray(dismissedZones) ? dismissedZones : [];

  if (!timeZone || !isStale || dismissed.includes(timeZone)) return null;

  return (
    <OpBanner
      type="warning"
      testId="outdated-timezone-data-banner"
      title={t("Your browser's time zone information is out of date")}
      description={t(
        'Dates and times for {{timeZone}} may be saved or displayed an hour off. Update your browser to the latest version to correct this.',
        { timeZone },
      )}
      // `closeIcon` is what actually makes an object-form `closable` render a
      // button -- an object with only `onClose` is silently not closable.
      closable={{
        closeIcon: true,
        'aria-label': t('Close'),
        onClose: () => setDismissedZones([...dismissed, timeZone]),
      }}
    />
  );
};
